import { useEffect, useState } from "react";

// True while the element is on (or about to come on) screen AND the tab is
// visible. Used to pause animations that would otherwise run forever where
// nobody can see them (2026-10-08, speed Phase 0).
//
// Measured on the live homepage, a 412px phone at 4x CPU slowdown, idle:
// the main thread was busy 7.8 s of every 8 s. 300 skeleton shimmers on
// lazy rails far below the fold cost ~2.9 s of that and the category
// marquee ~2.7 s, all for animations nobody was looking at.
//
// "Can't tell" means "animate": with no IntersectionObserver (old browsers,
// jsdom) or no document, the element is treated as visible, so the worst
// case is today's behaviour, never an animation that fails to start.
const ROOT_MARGIN = "200px 0px";   // start a little before it scrolls into view

export default function useAnimateWhileVisible(ref) {
  const [onScreen, setOnScreen] = useState(true);
  const [tabVisible, setTabVisible] = useState(
    typeof document === "undefined" ? true : document.visibilityState !== "hidden"
  );

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") return undefined;
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => setOnScreen(e.isIntersecting)),
      { rootMargin: ROOT_MARGIN }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [ref]);

  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    const sync = () => setTabVisible(document.visibilityState !== "hidden");
    document.addEventListener("visibilitychange", sync);
    return () => document.removeEventListener("visibilitychange", sync);
  }, []);

  return onScreen && tabVisible;
}
